<script setup lang="ts">
import {computed, ref} from "vue";
import MedicationCheckupCopiloteLine from "@/medication-checkup/components/MedicationCheckupCopiloteLine.vue";
import MedicationCheckupUserEcho from "@/medication-checkup/components/MedicationCheckupUserEcho.vue";
import MedicationCheckupFollowUpGirerdCard from "@/medication-checkup/components/MedicationCheckupFollowUpGirerdCard.vue";
import MedicationCheckupCopiloteBubble from "@/medication-checkup/components/MedicationCheckupCopiloteBubble.vue";
import MedicationCheckupSceneReplies from "@/medication-checkup/components/MedicationCheckupSceneReplies.vue";
import {MedicationCheckupAction} from "@/medication-checkup/interfaces";
import {FollowUpGirerdView} from "@/medication-checkup/interfaces/follow-up-girerd";
import {SceneProps, useScene} from "@/medication-checkup/composables/useScene";
import {useGirerdAnswers} from "@/medication-checkup/composables/useGirerdAnswers";
import {useMissingAnswers} from "@/medication-checkup/composables/useMissingAnswers";

/**
 * The scene of the Girerd questionnaire of the adherence follow-up: the
 * adherence of the patient reassessed, as the Assurance Maladie form asks.
 *
 * Everything it shows is served by the handler: the words, the six
 * questions, the score of the collection and — once validated — the answers
 * given with the reading of the score. Answering is the scene's own state,
 * as in the Girerd of the collection, without treatments to name: the
 * closing action, held until the six are answered, submits them at once.
 */

const props = defineProps<SceneProps<FollowUpGirerdView>>();

const {bubbleShown, cardShown, clicked, view, text, actions, closing, acted, act: submit} = useScene(
    props,
    {card: true}
);

const questions = computed(() => view.value?.questions ?? []);

/**
 * The six answers given so far, by question ref — local until the closing
 * action submits them.
 */
const {answers, missing, answer} = useGirerdAnswers<boolean>(questions);

const root = ref<HTMLElement | null>(null);

const {blocked, flagged, warning, flag} = useMissingAnswers(missing, root);

/**
 * Take the closing action, submitting the six answers.
 */
const act = (action: MedicationCheckupAction) => submit(action, {
    answers: questions.value.map(question => ({
        question_ref: question.ref,
        yes_no: answers.value[question.ref]
    }))
});
</script>

<template>
  <div ref="root" class="flex flex-col gap-3.5">
    <template v-if="frozen">
      <MedicationCheckupCopiloteLine v-if="text" :text="text" />
      <MedicationCheckupFollowUpGirerdCard v-if="view" :view="view" frozen />
      <MedicationCheckupUserEcho v-if="acted" :text="acted" />
      <MedicationCheckupCopiloteLine v-if="closing" :text="closing" />
    </template>

    <template v-else>
      <MedicationCheckupCopiloteBubble
        :shown="bubbleShown"
        :text="text"
      />

      <MedicationCheckupFollowUpGirerdCard
        v-if="cardShown && view"
        :view="view"
        :answers="answers"
        :flagged="flagged"
        class="animate-message-in"
        @answer="answer"
      />

      <MedicationCheckupSceneReplies
        :clicked="clicked"
        :actions="actions"
        :shown="cardShown"
        :blocked="blocked"
        :warning="warning"
        @act="act"
        @blocked="flag"
      />
    </template>
  </div>
</template>
